import React from 'react';
import List from '../../cruds/List';
import View from '../../cruds/View';
import Edit from '../../cruds/Edit';
import { notNull, stringNotBlank, minValue } from '../../cruds/validators';
import Table, { Column } from '../../components/container/Table';
import { EditButton, ViewButton, DeleteButton } from '../../components/output/CrudButton';
import OutputValue from '../../components/output/OutputValue';
import OutputText, { OutputBoolean } from '../../components/output/OutputText';
import InputText from '../../components/input/InputText';
import InputNumber from '../../components/input/InputNumber';
import { booleanFormatter } from '../../functions/textFormatter';
import { fetchPost } from '../../cruds/remote';
import DownloadButton from '../../components/output/DownloadButton';
import RadioButtonGroup, { RadioButton } from '../../components/input/RadioButtonGroup';
import MainContainer from '../../components/MainContainer';
import { Switch, Route } from 'react-router-dom';
import WithRole, { withRoles } from '../../components/container/WithRole';

const ENTITY_NAME = 'phrases';
const ENTITY_ROUTE = 'r-phrases';
const TITLE = 'rPhrases.title';

const evaluate = (resource) => {
  return fetchPost(ENTITY_NAME + '/reevaluation-needed', resource);
};

const CrudEdit = props => {
  const validators = [
    stringNotBlank('code', 'rPhrases.err.code'),
    notNull('cutaneousRisk', 'rPhrases.err.cutaneousRisk'),
    notNull('inhalationRisk', 'rPhrases.err.inhalationRisk'),
    notNull('cancerousRisk', 'rPhrases.err.cancerousRisk'),
    notNull('mutagenRisk', 'rPhrases.err.mutagenRisk'),
    notNull('atexSubstance', 'rPhrases.err.atexSubstance'),
    minValue('hazardIndex', 1, 'hPhrases.err.hazardIndex'),
  ];
  const numberFormat = /^[0-9]\d*(\.\d\d?)?$/;
  return (
    <Edit {...props}
        title={TITLE}
        entityName={ENTITY_NAME}
        entityRoute={ENTITY_ROUTE}
        entityId={props.match.params.number}
        initialValue={{ type: 'R' }}
        validators={validators}
        preSubmit={(resource) => evaluate(resource)}
    >
      <InputText name="code" label="rPhrases.code" required/>
      <InputText name="description" label="rPhrases.description" />
      <InputNumber name="hazardIndex" label="rPhrases.hazardIndex" format={numberFormat} required />
      <RadioButtonGroup name="cutaneousRisk" label="rPhrases.cutaneousRisk.name" required>
        <RadioButton value="true" label="rPhrases.cutaneousRisk.true" />
        <RadioButton value="false" label="rPhrases.cutaneousRisk.false" />
      </RadioButtonGroup>
      <RadioButtonGroup name="inhalationRisk" label="rPhrases.inhalationRisk.name" required>
        <RadioButton value="true" label="rPhrases.inhalationRisk.true" />
        <RadioButton value="false" label="rPhrases.inhalationRisk.false" />
      </RadioButtonGroup>
      <RadioButtonGroup name="cancerousRisk" label="rPhrases.cancerousRisk.name" required>
        <RadioButton value="true" label="rPhrases.cancerousRisk.true" />
        <RadioButton value="false" label="rPhrases.cancerousRisk.false" />
      </RadioButtonGroup>
      <RadioButtonGroup name="mutagenRisk" label="hPhrases.mutagenRisk.name" required>
        <RadioButton value="true" label="hPhrases.mutagenRisk.true" />
        <RadioButton value="false" label="hPhrases.mutagenRisk.false" />
      </RadioButtonGroup>
      <RadioButtonGroup name="atexSubstance" label="rPhrases.atexSubstance.name" required>
        <RadioButton value="true" label="rPhrases.atexSubstance.true" />
        <RadioButton value="false" label="rPhrases.atexSubstance.false" />
      </RadioButtonGroup>
    </Edit>
  );
};

const CrudView = props => (
  <View title={TITLE} entityName={ENTITY_NAME} entityRoute={ENTITY_ROUTE} entityId={props.match.params.number}>
    <OutputText name="code" label="rPhrases.code" />
    <OutputText name="description" label="rPhrases.description" />
    <OutputText name="hazardIndex" label="rPhrases.hazardIndex" />
    <OutputBoolean name="cutaneousRisk" label="rPhrases.cutaneousRisk.name" />
    <OutputBoolean name="inhalationRisk" label="rPhrases.inhalationRisk.name" />
    <OutputBoolean name="cancerousRisk" label="rPhrases.cancerousRisk.name" />
    <OutputBoolean name="mutagenRisk" label="rPhrases.mutagenRisk.name" />
    <OutputBoolean name="atexSubstance" label="rPhrases.atexSubstance.name" />
  </View>
);

const exportButtons = (filters, context, toggleModal) => {
  return (
    <span>
      <DownloadButton from={ENTITY_NAME}
          filters={filters}
          type="application/vnd.ms-excel"
          extension="xls"
          tooltip="general.export"
          handleError={toggleModal}
      />
      <DownloadButton
          from={ENTITY_NAME}
          filters={filters}
          type="application/pdf"
          extension="pdf"
          tooltip="general.print"
          iconType="printer"
          handleError={toggleModal}
      />
    </span>
  );
};

const CrudList = props => {
  const table = <Table>
                  <Column label="rPhrases.code" orderingName="CODE">
                    <OutputValue name="code" />
                  </Column>
                  <Column label="rPhrases.description" orderingName="DESCRIPTION">
                    <OutputValue name="description" />
                  </Column>
                  <Column label="rPhrases.hazardIndex" orderingName="HAZARD_INDEX">
                    <OutputValue name="hazardIndex" />
                  </Column>
                  <Column label="rPhrases.cutaneousRisk.name" orderingName="CUTANEOUS_RISK">
                    <OutputValue name="cutaneousRisk" formatter={booleanFormatter} />
                  </Column>
                  <Column label="rPhrases.inhalationRisk.name" orderingName="INHALATION_RISK">
                    <OutputValue name="inhalationRisk" formatter={booleanFormatter} />
                  </Column>
                  <Column label="rPhrases.cancerousRisk.name" orderingName="CANCEROUS_RISK">
                    <OutputValue name="cancerousRisk" formatter={booleanFormatter} />
                  </Column>
                  <Column label="hPhrases.mutagenRisk.name" orderingName="MUTAGEN_RISK">
                    <OutputValue name="mutagenRisk" formatter={booleanFormatter} />
                  </Column>
                  <Column label="rPhrases.atexSubstance.name" orderingName="ATEX_SUBSTANCE">
                    <OutputValue name="atexSubstance" formatter={booleanFormatter} />
                  </Column>
                  <Column>
                    <WithRole roles={['A', 'B']}>
                      <EditButton entityRoute={props.match.url} />
                    </WithRole>
                    <ViewButton entityRoute={props.match.url} />
                    <WithRole roles={['A', 'B']}>
                      <DeleteButton />
                    </WithRole>
                  </Column>
                </Table>;

  return <List {...props}
                title={TITLE}
                entityName={ENTITY_NAME}
                entityRoute={ENTITY_ROUTE}
                component={table}
                filters={[
                  { name: 'fullSearch', placeholder: 'general.fullSearch' },
                  { name: 'type', defaultValue: 'R' },
                ]}
                additionalButtons={exportButtons}
                defaultOrdering="CODE"
                hideAddButton = {!withRoles(['A', 'B'])}
        />;
};

const HPhrases = props => {
  const basePath = props.match.url;
  return (
    <MainContainer>
      <Switch>
        <Route exact path={basePath} component={CrudList} />
        <Route exact path={basePath + '/view/:number'} component={CrudView} />
        <Route exact path={basePath + '/edit/:number'} render={props => <CrudEdit {...props} />} />
        <Route exact path={basePath + '/add'} render={props => <CrudEdit {...props} />} />
      </Switch>
    </MainContainer>
  );
};

export default HPhrases;



// WEBPACK FOOTER //
// ./src/main/pages/registry/RPhrases.js